import i18n from '@/utils/i18n';
import { AlertLevel, CreditMemoInput, InvoiceInput } from '@/types';
import { captureException } from '@/utils/sentry';
import { PaymentMethodType } from '@/enums';
import { useCallback } from 'react';
import { useInitiateKlarnaPayment } from '@/hooks/payments/use-initiate-klarna-payment';
import { useKlarnaSdk } from '@/hooks/payments/use-klarna-sdk';
import { useKlarnaStore, variantOf } from '@/stores/use-klarna-store';
import { useToasts } from '@/contexts/toast-context';

interface UseKlarnaWebFlowArgs {
  onKlarnaPayment: () => void;
  refreshQueries: () => void;
  restaurantUUID: string;
  supplierUUID: string;
}

interface StartFlowArgs {
  creditMemos: CreditMemoInput[];
  feesInMicros: number;
  invoices: InvoiceInput[];
}

/**
 * Runs the Klarna payment flow on the web when the user taps "Continue
 * with Klarna."
 *
 * This flow initiates and then shows the Klarna UI. Adyen auto-captures
 * once Klarna authorizes, so no explicit capture call is needed.
 *
 * @param onKlarnaPayment - Runs after Klarna approves the payment.
 * @param refreshQueries - Function to refresh unpaid invoices and payment history.
 * @param restaurantUUID - Restaurant paying.
 * @param supplierUUID - Supplier being paid.
 * @returns `{ startFlow }`. Starts the flow. Called with the selected
 *   invoices, credit memos, and transaction fee.
 *
 * @example
 * const { startFlow: runKlarnaWebFlow } = useKlarnaWebFlow({
 *   onKlarnaPayment: () => setShowSuccessModal(true),
 *   refreshQueries,
 *   restaurantUUID,
 *   supplierUUID,
 * });
 * await runKlarnaWebFlow({ invoices, creditMemos, feesInMicros });
 */
export const useKlarnaWebFlow = ({
  onKlarnaPayment,
  refreshQueries,
  restaurantUUID,
  supplierUUID,
}: UseKlarnaWebFlowArgs) => {
  const klarnaSelection = useKlarnaStore((s) => s.selection);
  const clearKlarnaSelection = useKlarnaStore((s) => s.clearKlarnaSelection);

  const [initiate] = useInitiateKlarnaPayment();
  const { authorize } = useKlarnaSdk();

  const { showErrorToast } = useToasts();

  const startFlow = useCallback(
    async ({
      creditMemos,
      feesInMicros,
      invoices,
    }: StartFlowArgs): Promise<boolean> => {
      const variant = variantOf(klarnaSelection);
      if (!variant) {
        return false;
      }

      const paymentMethodJson = JSON.stringify({
        data: {
          paymentMethod: {
            subtype: 'sdk',
            type:
              variant === PaymentMethodType.KLARNA
                ? 'klarna'
                : 'klarna_account',
          },
        },
      });

      const { data } = await initiate({
        variables: {
          creditMemos,
          feesInMicros,
          invoices,
          paymentMethod: paymentMethodJson,
          restaurantId: restaurantUUID,
          returnUrl:
            variant === PaymentMethodType.KLARNA_ACCOUNT
              ? window.location.href
              : undefined,
          supplierId: supplierUUID,
        },
      });
      const response = data?.initiateKlarnaPayment;

      if (
        !response ||
        response.alert_level !== AlertLevel.Positive ||
        !response.payment_batch_id
      ) {
        showErrorToast(response?.alert_message ?? undefined);
        return false;
      }
      if (!response.klarna_action) {
        captureException(
          new Error('Klarna initiate: Positive response missing klarna_action'),
          { extra: { paymentBatchId: response.payment_batch_id } },
        );
        showErrorToast(i18n.t('klarna.unavailable'));
        return false;
      }

      const action = JSON.parse(response.klarna_action);

      if (
        action?.type !== 'sdk' ||
        !action.sdkData?.client_token ||
        !action.sdkData?.payment_method_category ||
        typeof action.paymentData !== 'string'
      ) {
        captureException(
          new Error(
            'Klarna initiate: Positive response with malformed klarna_action',
          ),
          {
            extra: {
              actionType: action?.type,
              paymentBatchId: response.payment_batch_id,
            },
          },
        );
        showErrorToast(i18n.t('klarna.unavailable'));
        return false;
      }

      const sdk = {
        clientToken: action.sdkData.client_token,
        paymentData: action.paymentData,
        paymentMethodCategory: action.sdkData.payment_method_category,
      };

      // Loads the Klarna UI and waits for the user to complete payment.
      const authResponse = await authorize({
        category: sdk.paymentMethodCategory,
        clientToken: sdk.clientToken,
      });

      if (authResponse.approved && authResponse.authorization_token) {
        onKlarnaPayment();
        return true;
      }

      refreshQueries();

      if (authResponse.error) {
        showErrorToast();
        return false;
      }

      if (!authResponse.show_form) {
        clearKlarnaSelection();
        showErrorToast();
      }
      return false;
    },
    [
      authorize,
      clearKlarnaSelection,
      initiate,
      klarnaSelection,
      onKlarnaPayment,
      refreshQueries,
      restaurantUUID,
      showErrorToast,
      supplierUUID,
    ],
  );

  return { startFlow };
};
